import type { ComponentConfigType } from './utils/data-parser';
import type { IBaseTableParams, JsonFieldType } from '../types';
import { CheckboxOptionType } from 'antd';
import { ITableColumnMenuOptions } from '../components';

export interface IQueryChangeParams extends IBaseTableParams {
  filters?: any;
}

export enum ComponentType {
  Box = 'Box',
  Loop = 'Loop',
  Form = 'Form',
  FormGrid = 'FormGrid',
  FormGridColumn = 'FormGrid.GridColumn',
  FormInput = 'Input',
  FormInputPassword = 'Input.Password',
  FormInputSearch = 'Input.Search',
  FormSelect = 'Select',
  FormCheckbox = 'Checkbox',
  FormTextarea = 'Input.TextArea',
  FormRadio = 'Radio',
  FormRadioGroup = 'Radio.Group',
  FormSwitch = 'Switch',
  FormDatePicker = 'DatePicker',
  FormDateRangePicker = 'DatePicker.RangePicker',
  FormTimePicker = 'TimePicker',
  FormUpload = 'Upload',
  FormSlider = 'Slider',
  FormRate = 'Rate',
  FilesUpload = 'FilesUpload',
  FormFilesUpload = 'FormFilesUpload',
  FormEditor = 'FormEditor',
  FormReferenceSelect = 'FormReferenceSelect',
  FormReferenceTreeSelect = 'FormReferenceTreeSelect',
  FormReferenceTreeSelectAsync = 'FormReferenceTreeSelectAsync',
  FormTriggerActionButton = 'FormTriggerActionButton',
  FormArrayTable = 'ArrayTable',
  FlowChart = 'FlowChart',
  TriggerActionButton = 'TriggerActionButton',
  Title = 'Title',
  ChildContainer = 'ChildContainer',
  SinglePage = 'SinglePage',
  ListPage = 'ListPage',
  CreatePage = 'CreatePage',
  Custom = 'Custom',
  Flex = 'Flex',
  FormFlex = 'FormFlex',
  FormTitle = 'FormTitle',
  FormText = 'FormText',
  FormLink = 'FormLink',
  FormImportInstance = 'FormImportInstance',
  FormBox = 'FormBox',
  Space = 'Space',
  Text = 'Text',
  Html = 'Html',
  Link = 'Link',
  SimpleTriggerActionButton = 'SimpleTriggerActionButton',
  Button = 'Button',
  DownloadFile = 'DownloadFile',
  Table = 'Table',
  TreeTable = 'TreeTable',
  Filters = 'Filters',
  Editor = 'Editor',
  TemplateEditor = 'TemplateEditor',
  NumberPicker = 'NumberPicker',
  Tabs = 'Tabs',
  TabPane = 'Tabs.TabPane',
  FormTabs = 'FormTabs',
  FormTabPane = 'FormTabs.TabPane',
  Avatar = 'Avatar',
  Alert = 'Alert',
  FileViewer = 'FileViewer',
  FilePanel = 'FilePanel',
  Divider = 'Divider',
  Card = 'Card',
  Tag = 'Tag',
  DateTime = 'DateTime',
  FormRecursionItem = 'FormRecursionItem',
  EpiBuilder = 'ePIBuilder',
  FormChunkUpload = 'FormChunkUpload',
  LoadableData = 'LoadableData',
  Icon = 'Icon',
  FolderPreview = 'FolderPreview',
  DocumentComparison = 'DocumentComparison',
  TogglePanels = 'TogglePanels',
  TogglePanel = 'TogglePanels.Panel',
  Collapse = 'Collapse',
  CollapsePanel = 'Collapse.Panel',
  Tooltip = 'Tooltip',
  CompareWorkspace = 'CompareWorkspace',
  CompareWorkspaceCustomPanel = 'CompareWorkspace.CustomPanel',
}

export enum ComponentCategory {
  General = 'General',
  Layout = 'Layout',
  FormControls = 'Form Controls',
  ePI = 'ePI',
}

export interface IComponentDef {
  name: ComponentType;
  category: string;
  title: string;
  droppable?: boolean;
}

export interface IEnumOption {
  label: string;
  value: any;
  children?: any[];
}

export type IEnumOptions =
  | (string | number | CheckboxOptionType<any>)[]
  | IEnumOption[];

export interface IComponentSchema<T> {
  type: JsonFieldType; // void, object, array, string, number, boolean, etc.
  name: string; // name of the component
  description?: string; // description of the component
  required?: boolean; // if the component is required
  title?: string; // title of the component
  default?: any; // default value of the component
  'x-designable-id': string; // id of the component
  'x-index': number; // index of the component in parent component
  'x-component'?: ComponentType; // component type
  'x-component-props'?: T; // component props
  'x-component-props-config'?: any; // component builder props
  properties?: Record<string, IComponent<any>>; // children of the component if exists TODO: add type here
  config?: any; // component config
}

export type IComponent<T> = IFormFieldSchema<T> | IComponentSchema<T>;

export interface IFormFieldSchema<T> extends IComponentSchema<T> {
  'x-display'?: 'hidden' | 'visible';
  'x-validator'?: any[];
  'x-decorator'?: string;
  'x-decorator-props'?: any; // TODO: add type here
  'x-decorator-props-config'?: any; // TODO: add type here
  enum?: IEnumOptions;
}

export interface IDraftDetails {
  enabled: boolean;
  draftableId?: string;
  formName?: string;
  labelText?: string;
  discardText?: string;
  discardTitle?: string;
  discardButtonConfirmText?: string;
  discardButtonCancelText?: string;
}

export interface IFormilySchema extends IComponentSchema<IFormProps> {}

export interface IFormProps {
  labelCol?: number;
  wrapperCol?: number;
  initialValues?: Record<string, any>;
  editable?: boolean;
  visible?: boolean;
  draft?: IDraftDetails;
  draftableId?: string;
  draftFormName?: string;
  draftTagStyle?: React.CSSProperties;
}

export interface IConfigGroup {
  title: string;
  items?: IConfigItem[];
  component?: string;
  props?: any;
}

export interface ITooltip {
  key: string;
  value: string;
}

export interface IConfigItem {
  name: string;
  label: string;
  valueComponent: any;
  configTypes: ComponentConfigType[];
  tooltips?: ITooltip[];
}

export interface IConfig {
  component: string;
  configGroups: IConfigGroup[];
}

export interface IColumn {
  id: string;
  title: string;
  dataIndex: string | string[];
  format: ColumnFormat;
  value?: string;
  align?: 'left' | 'center' | 'right';
  width?: number;
  fixed?: 'left' | 'right' | false;
  ellipsis?: boolean;
  sorter?: boolean;
  sortName?: string;
  filterable?: boolean;
  filterName?: string;
  defaultVisible?: boolean;
  columnMenuOptions?: ITableColumnMenuOptions;
  customComponentSchema?: IComponentSchema<any> | null;
  tranformer?: string;
  defaultValue?: string;
  order?: number;
  visible?: boolean;
}

/** One root folder in `CompareWorkspace`'s "Nodes" builder panel — `selector`
 * is a raw JMESPath expression (evaluated against `PageContext`, same `@`
 * syntax as elsewhere) producing that folder's `children`. */
export interface ICompareWorkspaceRootNode {
  id: string;
  name: string;
  selector: string;
  counter?: boolean;
  /** Tags shown on the row. Keyed by row id (like `rootNodes`) so each field
   * resolves individually. */
  tags?: Record<string, ICompareWorkspaceRootNodeTag>;
  info?: string;
  order?: number;
}

export interface ICompareWorkspaceRootNodeTag {
  id: string;
  label: string;
  tone?: 'neutral' | 'info' | 'success' | 'warning' | 'danger';
  /** Overrides `tone`. */
  color?: string;
  order?: number;
}

export enum ColumnFormat {
  STRING = 'string',
  TAG = 'tag',
  BOOLEAN = 'boolean',
  DATE = 'date',
  DATETIME = 'datetime',
  TIME = 'time',
  CUSTOM = 'custom',
  REFERENCE = 'reference',
}

export interface IModelInstance {
  id: string;
  modelId: string;
  modelVersionId: string;
  data: any;
  version: number;
}
